import React from 'react';
import styled, { ThemeContext } from 'styled-components';

import { Dropdown, DropdownList } from '../../';

import { DropdownSelectTrigger } from './DropdownSelectTrigger';
import { DropdownSelectTriggerLabel } from './DropdownSelectTriggerLabel';
import { DropdownSelectOptionProps } from './DropdownSelectOption';
import { DropdownSelectSearch } from './DropdownSelectSearch';
import { processOptions, onFilterChange } from './utils';
import FocusTrap from 'focus-trap-react';

export type OptionTypeProps<T> = {
    value?: T;
    label?: string | React.ReactNode;
    disabled?: boolean;
    icon?: JSX.Element;
    onChange?: (value: T) => void;
};

type OptionType<T> = OptionTypeProps<T> & {
    height?: number;
    Component?: React.FC<DropdownSelectOptionProps<T>>;
};

export type DropdownSelectOptionType<T> = OptionType<T> & {
    value?: T;
    options?: OptionType<T>[];
};

export type Matcher<T> = (inputValue: string, option: DropdownSelectOptionType<T>) => boolean;

type Props<T> = {
    value?: T;
    options: DropdownSelectOptionType<T>[];
    numberOfItemsToShow?: number;
    shouldCloseOnSelect?: boolean;
    placeholder?: string | JSX.Element;
    searchPlaceholder?: string;
    noMatchAlert?: string | JSX.Element;
    portalRoot?: string;
    disabled?: boolean;
    filter?: boolean;
    className?: string;
    onChange?: (value: T) => void;
    filterFunction?: Matcher<T>;
    dataLookup?: string;
    fitItems?: boolean;
    outline?: boolean;
    align?: 'left' | 'right';
    backgroundColor?: string;
};

const Wrapper = styled.div`
    position: relative;
`;

const DropdownWrap = styled.div`
    position: relative;

    & > div {
        width: 100%;
    }
`;

export function DropdownSelect<T>({
    value,
    options,
    placeholder,
    searchPlaceholder = '',
    noMatchAlert,
    numberOfItemsToShow = 6,
    disabled = false,
    filter = false,
    shouldCloseOnSelect = true,
    portalRoot,
    className,
    onChange = () => {},
    filterFunction = (term: string, item: any): any => item.label?.toLowerCase().includes(term.toLowerCase()),
    dataLookup,
    fitItems,
    outline = true,
    align = 'left',
    backgroundColor,
}: Props<T>) {
    const { bento } = React.useContext(ThemeContext);
    const parentRef = React.useRef<HTMLDivElement | null>(null);
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const [isOpen, setOpen] = React.useState(false);
    const [containerHeight, setContainerHeight] = React.useState(0);
    const [filteredOptions, setFilteredOptions] = React.useState(options);

    React.useEffect(() => setFilteredOptions(options), [options]);

    const onCloseDropdown = () => {
        setOpen(false);
        timeoutRef.current = setTimeout(() => {
            // reset options on close
            setFilteredOptions(options);
        }, bento.transition * 1000);
    };

    const onItemChange = React.useCallback((newValue: T) => {
        onChange(newValue);
        if (shouldCloseOnSelect) {
            onCloseDropdown();
        }
    }, []);

    const { heightMap, displayLabel, children } = processOptions<T>(filteredOptions, onItemChange, value);

    React.useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    React.useEffect(() => {
        const newHeight = Array.from(heightMap).reduce((acc, [key, value]) => {
            if (key <= numberOfItemsToShow) {
                return acc + value;
            }
            return acc;
        }, 0);
        setContainerHeight(newHeight);
    }, [numberOfItemsToShow, heightMap.size]);

    return (
        <Wrapper className={className}>
            <DropdownSelectTrigger
                ref={parentRef}
                disabled={disabled}
                isOpen={isOpen}
                setOpen={setOpen}
                outline={outline}
                backgroundColor={backgroundColor}
            >
                <DropdownSelectTriggerLabel data-placeholder={!displayLabel} data-lookup={dataLookup} align={align}>
                    {displayLabel || placeholder}
                </DropdownSelectTriggerLabel>
            </DropdownSelectTrigger>
            <DropdownWrap>
                <Dropdown
                    isOpen={isOpen}
                    parentRef={parentRef}
                    onClose={onCloseDropdown}
                    portalRoot={portalRoot}
                    fullWidth={!fitItems}
                >
                    <FocusTrap
                        focusTrapOptions={{
                            allowOutsideClick: true,
                            onDeactivate: onCloseDropdown,
                            fallbackFocus: document.body,
                        }}
                    >
                        <div>
                            {filter && (
                                <DropdownSelectSearch
                                    placeholder={searchPlaceholder}
                                    noMatchAlert={filteredOptions.length === 0 ? noMatchAlert : undefined}
                                    onChange={term => setFilteredOptions(onFilterChange(term, filterFunction, options))}
                                />
                            )}
                            <DropdownList getItemHeight={index => heightMap.get(index) || 0} height={containerHeight}>
                                {children}
                            </DropdownList>
                        </div>
                    </FocusTrap>
                </Dropdown>
            </DropdownWrap>
        </Wrapper>
    );
}
